/**
 * Broadcast Banner Component
 *
 * Displays command broadcasts in sector rooms with prominent styling.
 */

import type { CommandBroadcast, EscalationPriority } from '@/lib/types'

interface BroadcastBannerProps {
  broadcast: CommandBroadcast
  onDismiss?: () => void
}

export function BroadcastBanner({ broadcast, onDismiss }: BroadcastBannerProps) {
  const priorityStyles: Record<EscalationPriority, {
    bg: string
    border: string
    icon: string
    text: string
  }> = {
    critical: {
      bg: 'bg-red-500/20',
      border: 'border-red-500',
      icon: '🚨',
      text: 'text-red-400',
    },
    urgent: {
      bg: 'bg-orange-500/20',
      border: 'border-orange-500',
      icon: '⚠️',
      text: 'text-orange-400',
    },
    routine: {
      bg: 'bg-blue-500/20',
      border: 'border-blue-500',
      icon: '📢',
      text: 'text-blue-400',
    },
  }

  const style = priorityStyles[broadcast.priority]
  const timeAgo = getTimeAgo(broadcast.timestamp)

  const getMessageContent = (): string => {
    if (broadcast.message.type === 'text') {
      return (broadcast.message as { content: string }).content
    }
    if (broadcast.message.type === 'voice') {
      const voiceMsg = broadcast.message as { transcript?: string; duration: number }
      return voiceMsg.transcript || `Voice message (${voiceMsg.duration}s)`
    }
    return 'Message'
  }

  return (
    <div className={`${style.bg} border-l-4 ${style.border} rounded-r-lg p-3 mb-2`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className={`flex items-center gap-2 ${style.text} text-sm font-bold`}>
            <span>{style.icon}</span>
            <span>COMMAND BROADCAST</span>
            {broadcast.priority !== 'routine' && (
              <span className="uppercase text-xs px-1.5 py-0.5 rounded bg-white/10">
                {broadcast.priority}
              </span>
            )}
          </div>
          <p className="text-white mt-1">{getMessageContent()}</p>
          <p className="text-xs text-white/50 mt-1">
            From {broadcast.broadcastByName} • {timeAgo}
          </p>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-white/40 hover:text-white/60 transition-colors"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  )
}

interface BroadcastListProps {
  broadcasts: CommandBroadcast[]
  maxVisible?: number
  onDismiss?: (broadcastId: string) => void
}

export function BroadcastList({ broadcasts, maxVisible = 3, onDismiss }: BroadcastListProps) {
  const recentBroadcasts = broadcasts
    .slice()
    .sort((a, b) => {
      const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime()
      const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime()
      return timeB - timeA
    })
    .slice(0, maxVisible)

  if (recentBroadcasts.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      {recentBroadcasts.map((broadcast) => (
        <BroadcastBanner
          key={broadcast.id}
          broadcast={broadcast}
          onDismiss={onDismiss ? () => onDismiss(broadcast.id) : undefined}
        />
      ))}
    </div>
  )
}

function getTimeAgo(date: Date | string | number): string {
  const timestamp = date instanceof Date ? date.getTime() : new Date(date).getTime()
  const seconds = Math.floor((Date.now() - timestamp) / 1000)

  if (seconds < 60) return 'Just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

export default BroadcastBanner
